/**
 * Technik-Systemcheckliste (Arbeitsanweisung Software).
 *
 * 01.09.2026 – Modal, das der Techniker abarbeiten muss, bevor er in der
 * Auftrags-Checkliste den Punkt „System konfigurieren und in Betrieb
 * nehmen" abhaken darf. Enthält 34+ Punkte gruppiert in Sektionen, VRM
 * ID Eingabe (wird gegen das Produktbegleitdokument geprüft) und
 * PDF-Download.
 */
import { useEffect, useState } from 'react';
import { authFetch } from '../lib/auth-fetch';
import { X, Download, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

export default function TechnikChecklistModal({ orderId, onClose, onCompleted }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vrmDraft, setVrmDraft] = useState('');
  const [nameDraft, setNameDraft] = useState('');
  // 02.09.2026: „Prüfen"-Button erst nach mindestens 1× Klick ergibt
  // eine Meldung, sonst zeigt das Modal beim Öffnen einfach die
  // erwartete VRM-ID an — ohne Fehlermeldung.
  const [vrmAttempted, setVrmAttempted] = useState(false);
  // Force-Complete-Dialog (Systemcheckliste unvollständig / mit Fehler
  // abschließen — Hinweistext ist Pflicht).
  const [forceOpen, setForceOpen] = useState(false);
  const [forceReason, setForceReason] = useState('');

  const load = async () => {
    setLoading(true);
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/technik-checklist`);
    if (r.ok) {
      const d = await r.json();
      setState(d);
      setVrmDraft(d.vrmId || '');
      setNameDraft(d.employeeName || '');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [orderId]); // eslint-disable-line

  const save = async (patch) => {
    setSaving(true);
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/technik-checklist`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    setSaving(false);
    if (r.ok) {
      const d = await r.json();
      setState(d);
      // 01.09.2026 (Bug-Fix): Elternteil nur benachrichtigen, wenn die
      // Systemcheckliste jetzt „vollständig + VRM ok" ist — sonst
      // triggern wir bei jedem Häkchen einen kompletten Modal-Reload
      // im Parent (Auftragsdetails), was das Modal unmountet.
      if (d.allDone && d.vrmVerified) {
        onCompleted?.(d);
      }
      return d;
    }
    toast.error('Speichern fehlgeschlagen');
    return null;
  };

  const toggle = async (key, done) => {
    await save({ items: [{ key, done }] });
  };

  const saveVrm = async () => {
    setVrmAttempted(true);
    const d = await save({ vrmId: vrmDraft.trim() });
    if (d?.vrmVerified) toast.success('VRM ID geprüft — passt zum Begleitdokument');
    else if (d && vrmDraft.trim()) toast.warning('VRM ID stimmt nicht mit dem Begleitdokument überein');
  };

  const forceComplete = async () => {
    if (!forceReason.trim()) {
      toast.error('Bitte einen Hinweis eintragen (was fehlt / ist fehlgeschlagen).');
      return;
    }
    setSaving(true);
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/technik-checklist/force-complete`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: forceReason.trim() }),
    });
    setSaving(false);
    if (r.ok) {
      const d = await r.json();
      setState(d);
      setForceOpen(false);
      onCompleted?.(d);
      toast.success('Systemcheckliste abgeschlossen — Hinweis wurde im PDF hinterlegt.');
    } else {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || 'Abschluss fehlgeschlagen');
    }
  };

  const saveName = async () => {
    if (nameDraft.trim() !== (state?.employeeName || '')) {
      await save({ employeeName: nameDraft.trim() });
    }
  };

  const downloadPdf = async () => {
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/technik-checklist/pdf`);
    if (!r.ok) { toast.error('PDF-Download fehlgeschlagen'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Systemcheckliste_${orderId.slice(0,8)}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  if (loading || !state) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
        <div className="bg-white rounded-xl p-6 text-sm text-gray-500">Lade Systemcheckliste …</div>
      </div>
    );
  }

  const bySection = state.items.reduce((acc, it) => {
    (acc[it.section] = acc[it.section] || []).push(it); return acc;
  }, {});
  const doneCount = state.items.filter(it => it.done).length;
  const total = state.items.length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col"
           onClick={e => e.stopPropagation()}
           data-testid="technik-checklist-modal">
        <div className="p-4 border-b flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase text-gray-400 tracking-wide">Arbeitsanweisung Software</div>
            <h2 className="text-base font-semibold text-[#32373c]">Systemcheckliste</h2>
            <div className="text-xs text-gray-500 mt-0.5">
              {doneCount}/{total} Punkte abgehakt
              {state.completedAt && ' · komplett abgeschlossen'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadPdf} disabled={saving}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50"
                    data-testid="btn-technik-pdf">
              <Download className="w-3 h-3" /> PDF
            </button>
            <button onClick={onClose} className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                    data-testid="btn-technik-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-4">
          {/* VRM ID Prüfung */}
          <div className={`rounded-lg border p-3 ${
            state.vrmVerified ? 'bg-emerald-50 border-emerald-200'
              : (vrmAttempted && !state.expectedVrmId) ? 'bg-amber-50 border-amber-200'
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-start gap-2">
              {state.vrmVerified
                ? <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5" />
                : <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5" />}
              <div className="flex-1 text-xs">
                <div className="font-semibold mb-1">VRM Portal ID</div>
                {state.expectedVrmId ? (
                  <div className="text-gray-600 mb-2">
                    Erwartet (aus Begleitdokument): <code className="bg-white px-1 py-0.5 rounded">{state.expectedVrmId}</code>
                  </div>
                ) : vrmAttempted ? (
                  <div className="text-amber-700 mb-2">
                    Keine VRM ID im Produktbegleitdokument hinterlegt — bitte dort ergänzen oder unten die Systemcheckliste mit Hinweis abschließen.
                  </div>
                ) : (
                  <div className="text-gray-500 mb-2">
                    Trage die VRM ID vom System ein und klicke auf „Prüfen" — sie wird gegen das Produktbegleitdokument abgeglichen.
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input value={vrmDraft} onChange={e => setVrmDraft(e.target.value)}
                    placeholder="VRM ID am System eingeben"
                    className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded font-mono"
                    data-testid="technik-vrm-input" />
                  <button onClick={saveVrm} disabled={saving}
                    className="text-xs px-3 py-1 rounded bg-[#9ec043] text-white hover:bg-[#7ca735] disabled:opacity-50"
                    data-testid="btn-technik-vrm-save">
                    Prüfen
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Techniker-Name */}
          <div className="flex items-center gap-2 text-xs">
            <label className="text-gray-500 shrink-0">Techniker:</label>
            <input value={nameDraft} onChange={e => setNameDraft(e.target.value)}
                   onBlur={saveName}
                   placeholder="Vor- und Nachname"
                   className="flex-1 px-2 py-1 border border-gray-200 rounded"
                   data-testid="technik-name-input" />
          </div>

          {/* Checkliste je Sektion */}
          {Object.entries(bySection).map(([section, items]) => (
            <div key={section} className="space-y-1">
              <div className="text-[10px] uppercase tracking-wide text-[#9ec043] font-semibold">{section}</div>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {items.map(it => (
                  <label key={it.key}
                         className="flex items-start gap-2 p-2 hover:bg-gray-50 cursor-pointer text-xs"
                         data-testid={`technik-item-${it.key}`}>
                    <input type="checkbox" checked={!!it.done}
                      onChange={e => toggle(it.key, e.target.checked)}
                      disabled={saving}
                      className="mt-0.5 accent-[#9ec043]" />
                    <span className={`flex-1 leading-snug ${it.done ? 'text-gray-400 line-through' : 'text-[#32373c]'}`}>
                      {it.label}
                    </span>
                    {it.doneBy && (
                      <span className="text-[9px] text-gray-400 shrink-0">
                        ✓ {it.doneBy.split('@')[0]}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="p-3 border-t bg-gray-50 flex flex-col gap-2 text-xs">
          {forceOpen ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2">
              <div className="text-amber-800 font-semibold">
                Systemcheckliste unvollständig abschließen
              </div>
              <div className="text-amber-700 text-[11px]">
                Trage einen Hinweis ein, was fehlt oder fehlgeschlagen ist. Der
                Hinweis wird im gespeicherten PDF unter „Dokumente" für die
                Auftragsakte festgehalten.
              </div>
              <textarea value={forceReason} onChange={e => setForceReason(e.target.value)}
                        placeholder="z. B. VRM ID am System nicht abgleichbar / PV-Anlage vor Ort noch nicht angeschlossen"
                        rows={3}
                        className="w-full px-2 py-1.5 text-xs border border-amber-300 rounded resize-none"
                        data-testid="technik-force-reason" />
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => { setForceOpen(false); setForceReason(''); }}
                        className="text-xs px-3 py-1 rounded border border-gray-200 hover:bg-white"
                        data-testid="btn-technik-force-cancel">
                  Abbrechen
                </button>
                <button onClick={forceComplete} disabled={saving || !forceReason.trim()}
                        className="text-xs px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                        data-testid="btn-technik-force-confirm">
                  Abschließen mit Hinweis
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div className={
                state.forceCompleted ? 'text-amber-700 font-semibold'
                  : (state.allDone && state.vrmVerified) ? 'text-emerald-700 font-semibold'
                  : 'text-amber-700'}>
                {state.forceCompleted
                  ? '⚠ Mit Hinweis abgeschlossen — Auftrags-Checkpunkt kann abgehakt werden.'
                  : (state.allDone && state.vrmVerified)
                    ? '✓ Systemcheckliste komplett — Auftrags-Checkpunkt kann abgehakt werden.'
                    : 'Bitte alle Punkte abhaken UND VRM ID prüfen — oder unten mit Hinweis abschließen.'}
              </div>
              <div className="flex items-center gap-2">
                {!state.forceCompleted && !(state.allDone && state.vrmVerified) && (
                  <button onClick={() => setForceOpen(true)}
                          className="text-xs px-3 py-1.5 rounded border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
                          data-testid="btn-technik-force-open">
                    Mit Hinweis abschließen
                  </button>
                )}
                <button onClick={onClose}
                        className="text-xs px-3 py-1.5 rounded bg-[#32373c] text-white hover:bg-black"
                        data-testid="btn-technik-modal-close">
                  Schließen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
